import {
  ClusterOutlined,
  DeleteOutlined,
  EditOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { Avatar, Pagination, Popover, Space, Table, Tag, Tooltip } from "antd";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import DeleteTopPlanApi from "../../api/DeleteTopPlanApi";
import {
  openNotificationError,
  openNotificationSuccess,
} from "../../helpers/NotificationHelpers";
import { permissionAbility } from "../../helpers/permissionHelpers";
import EditSplitPlanModal from "../modal/edit/EditSplitPlanModal";
import HardDeleteModal from "../modal/HardDeleteModal";
import SplitPlanInfoModal from "../modal/info/SplitPlanInfoModal";
import SplitPlanModal from "../modal/SplitPlanModal";
import { LIMIT_PER_PAGE } from "../../helpers/constants";

function SplitsTable(props) {
  const [infoModal, setInfoModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [splitModal, setSplitModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState();

  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  let navigate = useNavigate();
  const positionState = useSelector((state) => state.positionState.value);
  const userLevel = positionState?.userRole?.name;

  useEffect(() => {
    setLoading(props.loading);
  }, [props.loading]);

  async function deletePlan() {
    const apiResponse = await DeleteTopPlanApi(selectedPlan?.planId);
    if (apiResponse.statusCode) {
      openNotificationError("bottomRight", apiResponse.message);
    } else if (apiResponse.message === "success") {
      openNotificationSuccess("bottomRight", "Top Plan deleted");
      if (props.data.plans?.length === 1) {
        props.changePage(page - 1);
      } else {
        props.changePage(page);
      }
    } else {
      openNotificationError("bottomRight", "Couldn't delete. Try again later.");
    }
    setSelectedPlan();
  }

  /*If page changed from parent component */
  useEffect(() => {
    setPage(props.page);
  }, [props.page]);

  const columns = [
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
    },
    {
      title: "Plan level",
      dataIndex: "planLevel",
      key: "planLevel",
      render: (text, record) => <Tag color={""}>{text}</Tag>,
    },
    {
      title: "Assign to",
      dataIndex: "assignTo",
      key: "assignTo",
      render: (text, record) => (
        <div className="flex items-center">
          {record.assignedTo?.imageUrl ? (
            <Avatar
              className="cursor-pointer"
              src={record.assignedTo?.imageUrl}
            />
          ) : (
            <Avatar>
              {record.assignedTo?.firstName ? (
                record.assignedTo?.firstName?.charAt(0).toUpperCase()
              ) : (
                <UserOutlined />
              )}
            </Avatar>
          )}
          <div className="ml-2">
            <div>{record.assignedTo?.firstName || "-"}</div>
            <div className="text-xs -mt-1">
              {record.assignedTo?.designationAbb}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: "Performance",
      dataIndex: "performance",
      key: "performance",
      render: (text, record) => (
        <Tag
          color={
            record.progressStatus === "On Track"
              ? "Green"
              : record.progressStatus === "Off Track"
              ? "Orange"
              : "Red"
          }
        >
          {(text || "0") + "%"}
        </Tag>
      ),
    },
    {
      title: "Action",
      dataIndex: "address",
      key: "address",
      render: (text, record) => (
        <Space size="middle">
          {permissionAbility(userLevel, "plans", "split") &&
            record.canSplit && (
              <Tooltip
                onClick={(event) => {
                  event.stopPropagation();
                  setSelectedPlan(record);
                  setSplitModal(true);
                }}
                title={"Split"}
              >
                <div className="text-blue-500 hover:text-blue-600 cursor-pointer">
                  <ClusterOutlined />
                </div>
              </Tooltip>
            )}
          {permissionAbility(userLevel, "plans", "edit") && (
            <Tooltip
              onClick={(event) => {
                event.stopPropagation();
                setSelectedPlan(record);
                setEditModal(true);
              }}
              title={"Edit"}
            >
              <div className="text-blue-500 hover:text-blue-600 cursor-pointer">
                <EditOutlined />
              </div>
            </Tooltip>
          )}
          {permissionAbility(userLevel, "plans", "delete") &&
            (record.canDelete ? (
              <Tooltip title={"Delete"}>
                <div
                  onClick={(event) => {
                    event.stopPropagation();
                    setSelectedPlan(record);
                    setDeleteModal(true);
                  }}
                  className="text-red-500 hover:text-red-600 cursor-pointer"
                >
                  <DeleteOutlined />
                </div>
              </Tooltip>
            ) : (
              <Popover
                placement="left"
                content={
                  <div className="text-xs w-48">
                    Can't delete this plan. This plan was cascaded.
                  </div>
                }
                trigger="hover"
              >
                <div className="text-gray-500">
                  <DeleteOutlined />
                </div>
              </Popover>
            ))}
        </Space>
      ),
    },
  ];

  return (
    <>
      <Table
        columns={columns}
        dataSource={props.data?.plans}
        pagination={false}
        loading={loading}
        onRow={(record) => {
          return {
            onClick: () => {
              setInfoModal(true);
              setSelectedPlan(record);
            },
          };
        }}
        rowClassName={"cursor-pointer hover:shadow"}
      />

      <div className="text-center mt-6">
        <Pagination
          onChange={(num) => {
            props.changePage(num);
            props.setPage(num);
          }}
          current={page}
          total={props.data?.totalCount || 0}
          pageSize={LIMIT_PER_PAGE}
        />
      </div>
      <SplitPlanInfoModal
        visible={infoModal}
        plan={selectedPlan}
        handleCancel={() => {
          setInfoModal(false);
          setSelectedPlan();
        }}
      />
      <SplitPlanModal
        visible={splitModal}
        plan={selectedPlan}
        handleCancel={() => {
          setSelectedPlan();
          setSplitModal(false);
        }}
        handleOk={() => props.changePage(page)}
      />
      <EditSplitPlanModal
        visible={editModal}
        handleCancel={() => {
          setSelectedPlan();
          setEditModal(false);
        }}
        plan={selectedPlan}
        handleOk={() => props.changePage(page)}
      />
      <HardDeleteModal
        visible={deleteModal}
        handleCancel={() => setDeleteModal(false)}
        handleOk={() => {
          deletePlan();
          setSelectedPlan();
        }}
        text={`Are you sure want to delete this plan?`}
        title={`Delete plan`}
      />
    </>
  );
}

export default SplitsTable;
